/**
 * Progressive create forms — few hops, optional one-pager toggle.
 * Use when formFields.length >= PROGRESSIVE_FIELD_THRESHOLD or formSteps is set.
 */

export const PROGRESSIVE_FIELD_THRESHOLD = 8;

/**
 * @typedef {{ id: string, title: string, fieldIds: string[] }} FormStepDef
 */

/**
 * @param {Array<{id: string}>} fields
 * @param {FormStepDef[]|undefined} formSteps
 * @returns {{ id: string, title: string, fields: object[] }[]}
 */
export const resolveFormSteps = (fields, formSteps) => {
  if (!fields?.length) {
    return [];
  }

  if (formSteps?.length) {
    const byId = Object.fromEntries(fields.map(f => [f.id, f]));
    const used = new Set();
    const steps = formSteps
      .map(step => {
        const stepFields = (step.fieldIds || []).map(id => byId[id]).filter(Boolean);
        stepFields.forEach(f => used.add(f.id));
        return {
          id: step.id,
          title: step.title,
          fields: stepFields,
        };
      })
      .filter(s => s.fields.length > 0);

    const leftover = fields.filter(f => !used.has(f.id));
    if (leftover.length && steps.length) {
      steps[steps.length - 1] = {
        ...steps[steps.length - 1],
        fields: [...steps[steps.length - 1].fields, ...leftover],
      };
    } else if (leftover.length) {
      steps.push({ id: 'more', title: 'More details', fields: leftover });
    }
    return steps;
  }

  if (fields.length < PROGRESSIVE_FIELD_THRESHOLD) {
    return [{ id: 'all', title: 'Details', fields }];
  }

  const chunkSize = Math.ceil(fields.length / Math.min(3, Math.ceil(fields.length / 6)));
  const titles = ['Basics', 'Details', 'Logistics'];
  const steps = [];
  for (let i = 0; i < fields.length; i += chunkSize) {
    const idx = steps.length;
    steps.push({
      id: `auto-${idx}`,
      title: titles[idx] || `Step ${idx + 1}`,
      fields: fields.slice(i, i + chunkSize),
    });
  }
  return steps;
};

export const shouldOfferProgressive = steps => steps.length > 1;

/**
 * @param {object[]} fields
 * @param {object} formData
 * @param {Record<string, string[]>} multiselect
 */
export const fieldsSatisfied = (fields, formData, multiselect = {}) =>
  fields.every(f => {
    if (!f.required) {
      return true;
    }
    if (f.type === 'multiselect') {
      return (multiselect[f.id] || []).length > 0;
    }
    return String(formData[f.id] || '').trim().length > 0;
  });
